import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Phone, Mail, Inbox, Upload, Check, X, Star, ImageOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";

const STATUS_LABELS = { new: "Nowe", in_progress: "W trakcie", done: "Zrealizowane" };
const STATUS_STYLES = {
    new: "bg-gold/15 text-ink border-gold/40",
    in_progress: "bg-blue-50 text-blue-700 border-blue-200",
    done: "bg-green-50 text-green-700 border-green-200",
};

function LoginForm() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Zalogowano pomyślnie");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-ink px-6">
            <div className="bg-noise pointer-events-none fixed inset-0 opacity-[0.05]" aria-hidden />
            <form
                data-testid="admin-login-form"
                onSubmit={submit}
                className="relative w-full max-w-md rounded-[2rem] border border-white/10 bg-white/[0.04] p-10 backdrop-blur-xl"
            >
                <p className="font-display text-3xl font-black tracking-tighter text-white">
                    Handy<span className="text-gold">&rsquo;</span>s
                </p>
                <p className="mt-2 text-sm text-white/50">Panel administratora — zaloguj się, aby zobaczyć zgłoszenia.</p>
                <div className="mt-8 space-y-5">
                    <div>
                        <label htmlFor="al-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Email</label>
                        <Input id="al-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@handys.pl" className="h-12 rounded-xl border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold" />
                    </div>
                    <div>
                        <label htmlFor="al-password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Hasło</label>
                        <Input id="al-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 rounded-xl border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold" />
                    </div>
                    {error && (
                        <p data-testid="admin-login-error" className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
                    )}
                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={loading}
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold uppercase tracking-wider text-ink transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zaloguj się"}
                    </button>
                    <Link data-testid="admin-back-home" to="/" className="block text-center text-xs text-white/40 transition-colors duration-300 hover:text-gold">
                        ← Wróć na stronę główną
                    </Link>
                </div>
            </form>
        </div>
    );
}

const REVIEW_STATUS_LABELS = { pending: "Oczekuje", approved: "Zatwierdzona", rejected: "Odrzucona" };
const REVIEW_STATUS_STYLES = {
    pending: "bg-gold/15 text-ink border-gold/40",
    approved: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
};
const UPLOAD_CATEGORIES = ["Łazienki", "Kuchnie", "Elektryka", "Hydraulika", "Malowanie", "Płytki"];

function GalleryPanel() {
    const [gallery, setGallery] = useState(null);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [cat, setCat] = useState(UPLOAD_CATEGORIES[0]);
    const [uploading, setUploading] = useState(false);

    const fetchGallery = useCallback(async () => {
        try {
            const { data } = await api.get("/gallery");
            setGallery(data);
        } catch {
            toast.error("Nie udało się pobrać galerii");
        }
    }, []);

    useEffect(() => {
        fetchGallery();
    }, [fetchGallery]);

    const upload = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Wybierz zdjęcie do wgrania");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", title.trim() || "Realizacja Handy's");
            fd.append("cat", cat);
            await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Zdjęcie dodane do galerii");
            setFile(null);
            setTitle("");
            fetchGallery();
        } catch (err) {
            toast.error("Nie udało się wgrać zdjęcia", {
                description: formatApiErrorDetail(err.response?.data?.detail),
            });
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/gallery/${id}`);
            setGallery((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== id) }));
            toast.success("Zdjęcie usunięte");
        } catch {
            toast.error("Nie udało się usunąć zdjęcia");
        }
    };

    const toggleStock = async () => {
        try {
            const { data } = await api.patch("/gallery/settings", { show_stock: !gallery.show_stock });
            setGallery((prev) => ({ ...prev, show_stock: data.show_stock }));
            toast.success(data.show_stock ? "Zdjęcia stockowe widoczne" : "Zdjęcia stockowe ukryte");
        } catch {
            toast.error("Nie udało się zmienić ustawienia");
        }
    };

    return (
        <div data-testid="gallery-panel">
            <form onSubmit={upload} className="mb-8 rounded-3xl border border-ink/8 bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-ink">Dodaj zdjęcie realizacji</h3>
                <p className="mt-1 text-sm text-ink/45">
                    Wgraj prawdziwe zdjęcie z remontu — pojawi się w galerii na stronie przed zdjęciami stockowymi.
                </p>
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label
                        data-testid="gallery-file-label"
                        className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-ink/15 bg-mist px-4 py-8 text-center text-sm text-ink/50 transition-colors duration-300 hover:border-gold/70 hover:text-ink md:row-span-2"
                    >
                        <Upload className="h-4 w-4 shrink-0" />
                        <span className="line-clamp-2">{file ? file.name : "Wybierz zdjęcie (JPG, PNG, WebP — maks. 10 MB)"}</span>
                        <input
                            data-testid="gallery-file-input"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </label>
                    <Input
                        data-testid="gallery-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Tytuł realizacji, np. Łazienka — ul. Kwiatowa"
                        maxLength={120}
                        className="h-12 rounded-xl border-ink/10 focus-visible:ring-gold"
                    />
                    <div className="flex gap-3">
                        <select
                            data-testid="gallery-category-select"
                            value={cat}
                            onChange={(e) => setCat(e.target.value)}
                            className="h-12 flex-1 rounded-xl border border-ink/10 bg-white px-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold"
                        >
                            {UPLOAD_CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <button
                            data-testid="gallery-upload-btn"
                            type="submit"
                            disabled={uploading}
                            className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-60"
                        >
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            Wgraj
                        </button>
                    </div>
                </div>
            </form>

            {gallery && (
                <div className="mb-8 flex items-center justify-between gap-4 rounded-2xl border border-ink/8 bg-white p-5">
                    <div>
                        <p className="font-display font-bold text-ink">Zdjęcia stockowe na stronie</p>
                        <p className="text-xs text-ink/45">Wyłącz, gdy wgrasz własne realizacje — strona pokaże tylko Twoje zdjęcia.</p>
                    </div>
                    <button
                        data-testid="gallery-stock-toggle"
                        onClick={toggleStock}
                        className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-semibold transition-colors duration-300 ${
                            gallery.show_stock ? "bg-ink text-white" : "bg-mist text-ink/50 ring-1 ring-ink/10"
                        }`}
                    >
                        {gallery.show_stock ? "Włączone" : "Wyłączone"}
                    </button>
                </div>
            )}

            {gallery === null ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-ink/30" />
                </div>
            ) : gallery.items.length === 0 ? (
                <div className="flex flex-col items-center rounded-3xl border border-dashed border-ink/15 bg-white py-16 text-center">
                    <ImageOff className="h-10 w-10 text-ink/20" />
                    <p className="mt-4 font-display text-xl font-bold text-ink">Brak własnych zdjęć</p>
                    <p className="mt-1 text-sm text-ink/45">Wgraj pierwsze zdjęcie realizacji powyżej.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {gallery.items.map((item) => (
                        <figure key={item.id} data-testid={`gallery-admin-item-${item.id}`} className="overflow-hidden rounded-2xl border border-ink/8 bg-white">
                            <img src={`${API}/gallery/file/${item.storage_path}`} alt={item.title} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                            <figcaption className="flex items-center justify-between gap-2 p-4">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-ink">{item.title}</p>
                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gold">{item.cat}</p>
                                </div>
                                <button
                                    data-testid={`gallery-admin-delete-${item.id}`}
                                    onClick={() => remove(item.id)}
                                    className="shrink-0 rounded-full border border-ink/10 p-2 text-ink/40 transition-[background-color,color,border-color] duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                    aria-label="Usuń zdjęcie"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            )}
            <PairsPanel />
        </div>
    );
}

function PairsPanel() {
    const [pairs, setPairs] = useState(null);
    const [beforeFile, setBeforeFile] = useState(null);
    const [afterFile, setAfterFile] = useState(null);
    const [pairTitle, setPairTitle] = useState("");
    const [pairUploading, setPairUploading] = useState(false);

    const fetchPairs = useCallback(async () => {
        try {
            const { data } = await api.get("/before-after");
            setPairs(data);
        } catch {
            toast.error("Nie udało się pobrać metamorfoz");
        }
    }, []);

    useEffect(() => {
        fetchPairs();
    }, [fetchPairs]);

    const upload = async (e) => {
        e.preventDefault();
        if (!beforeFile || !afterFile) {
            toast.error("Dodaj oba zdjęcia — przed i po");
            return;
        }
        setPairUploading(true);
        try {
            const fd = new FormData();
            fd.append("before", beforeFile);
            fd.append("after", afterFile);
            fd.append("title", pairTitle.trim() || "Metamorfoza");
            await api.post("/before-after", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Metamorfoza dodana — od razu widoczna na stronie");
            setBeforeFile(null);
            setAfterFile(null);
            setPairTitle("");
            fetchPairs();
        } catch (err) {
            toast.error("Nie udało się wgrać metamorfozy", {
                description: formatApiErrorDetail(err.response?.data?.detail),
            });
        } finally {
            setPairUploading(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/before-after/${id}`);
            setPairs((prev) => prev.filter((p) => p.id !== id));
            toast.success("Metamorfoza usunięta");
        } catch {
            toast.error("Nie udało się usunąć metamorfozy");
        }
    };

    const fileLabel = (file, text) => (
        <>
            <Upload className="h-4 w-4 shrink-0" />
            <span className="line-clamp-2">{file ? file.name : text}</span>
        </>
    );

    return (
        <div className="mt-12 border-t border-ink/8 pt-10" data-testid="pairs-panel">
            <form onSubmit={upload} className="mb-8 rounded-3xl border border-ink/8 bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-ink">Dodaj metamorfozę — przed / po</h3>
                <p className="mt-1 text-sm text-ink/45">
                    Wgraj parę zdjęć z tego samego remontu. Gdy dodasz własne metamorfozy, strona pokaże tylko je
                    (zamiast przykładowych).
                </p>
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label
                        data-testid="ba-before-label"
                        className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-ink/15 bg-mist px-4 py-7 text-center text-sm text-ink/50 transition-colors duration-300 hover:border-gold/70 hover:text-ink"
                    >
                        {fileLabel(beforeFile, "Zdjęcie PRZED remontem")}
                        <input
                            data-testid="ba-before-input"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => setBeforeFile(e.target.files?.[0] || null)}
                        />
                    </label>
                    <label
                        data-testid="ba-after-label"
                        className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gold/50 bg-gold/10 px-4 py-7 text-center text-sm text-ink/60 transition-colors duration-300 hover:border-gold hover:text-ink"
                    >
                        {fileLabel(afterFile, "Zdjęcie PO remoncie")}
                        <input
                            data-testid="ba-after-input"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => setAfterFile(e.target.files?.[0] || null)}
                        />
                    </label>
                    <Input
                        data-testid="ba-title-input"
                        value={pairTitle}
                        onChange={(e) => setPairTitle(e.target.value)}
                        placeholder="Tytuł, np. Kuchnia — Śródmieście"
                        maxLength={120}
                        className="h-12 rounded-xl border-ink/10 focus-visible:ring-gold"
                    />
                    <button
                        data-testid="ba-upload-btn"
                        type="submit"
                        disabled={pairUploading}
                        className="flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-60"
                    >
                        {pairUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Wgraj parę
                    </button>
                </div>
            </form>

            {pairs === null ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-ink/30" />
                </div>
            ) : pairs.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-ink/15 bg-white py-8 text-center text-sm text-ink/45">
                    Brak własnych metamorfoz — na stronie widać przykładowe pary.
                </p>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {pairs.map((p) => (
                        <figure key={p.id} data-testid={`ba-admin-item-${p.id}`} className="overflow-hidden rounded-2xl border border-ink/8 bg-white">
                            <div className="grid grid-cols-2">
                                <div className="relative">
                                    <img src={`${API}/gallery/file/${p.before_path}`} alt={`${p.title} — przed`} className="aspect-square w-full object-cover" loading="lazy" />
                                    <span className="absolute left-2 top-2 rounded-full bg-ink/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Przed</span>
                                </div>
                                <div className="relative">
                                    <img src={`${API}/gallery/file/${p.after_path}`} alt={`${p.title} — po`} className="aspect-square w-full object-cover" loading="lazy" />
                                    <span className="absolute right-2 top-2 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink">Po</span>
                                </div>
                            </div>
                            <figcaption className="flex items-center justify-between gap-2 p-4">
                                <p className="min-w-0 truncate text-sm font-semibold text-ink">{p.title}</p>
                                <button
                                    data-testid={`ba-admin-delete-${p.id}`}
                                    onClick={() => remove(p.id)}
                                    className="shrink-0 rounded-full border border-ink/10 p-2 text-ink/40 transition-[background-color,color,border-color] duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                    aria-label="Usuń metamorfozę"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReviewsPanel() {
    const [reviews, setReviews] = useState(null);

    const fetchReviews = useCallback(async () => {
        try {
            const { data } = await api.get("/reviews");
            setReviews(data);
        } catch {
            toast.error("Nie udało się pobrać opinii");
        }
    }, []);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/reviews/${id}`, { status });
            setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
            toast.success(`Status: ${REVIEW_STATUS_LABELS[status]}`);
        } catch {
            toast.error("Nie udało się zmienić statusu");
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/reviews/${id}`);
            setReviews((prev) => prev.filter((r) => r.id !== id));
            toast.success("Opinia usunięta");
        } catch {
            toast.error("Nie udało się usunąć opinii");
        }
    };

    if (reviews === null) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-ink/30" />
            </div>
        );
    }
    if (reviews.length === 0) {
        return (
            <div className="flex flex-col items-center rounded-3xl border border-dashed border-ink/15 bg-white py-16 text-center">
                <Star className="h-10 w-10 text-ink/20" />
                <p className="mt-4 font-display text-xl font-bold text-ink">Brak opinii</p>
                <p className="mt-1 text-sm text-ink/45">Opinie wystawione przez klientów na stronie pojawią się tutaj do zatwierdzenia.</p>
            </div>
        );
    }
    return (
        <div className="space-y-4" data-testid="reviews-panel">
            {reviews.map((r) => (
                <article key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-3xl border border-ink/8 bg-white p-6 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="flex flex-wrap items-center gap-3">
                                <h3 className="font-display text-lg font-bold text-ink">{r.name}</h3>
                                <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${REVIEW_STATUS_STYLES[r.status] || REVIEW_STATUS_STYLES.pending}`}>
                                    {REVIEW_STATUS_LABELS[r.status] || r.status}
                                </span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <span className="flex gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-gold text-gold" : "text-ink/15"}`} />
                                    ))}
                                </span>
                                <span className="text-xs text-ink/40">{r.job}</span>
                                <span className="text-xs text-ink/30">·</span>
                                <span className="text-xs text-ink/40">
                                    {new Date(r.created_at).toLocaleString("pl-PL", { dateStyle: "long", timeStyle: "short" })}
                                </span>
                            </div>
                        </div>
                        <button
                            data-testid={`admin-review-delete-${r.id}`}
                            onClick={() => remove(r.id)}
                            className="rounded-full border border-ink/10 p-2.5 text-ink/40 transition-[background-color,color,border-color] duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                            aria-label="Usuń opinię"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                    <p className="mt-4 rounded-2xl bg-mist p-4 text-sm leading-relaxed text-ink/70">&bdquo;{r.text}&rdquo;</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {r.status !== "approved" && (
                            <button
                                data-testid={`admin-review-approve-${r.id}`}
                                onClick={() => setStatus(r.id, "approved")}
                                className="flex items-center gap-1.5 rounded-full bg-green-600 px-4 py-2 text-xs font-semibold text-white transition-colors duration-300 hover:bg-green-700"
                            >
                                <Check className="h-3.5 w-3.5" /> Zatwierdź i publikuj
                            </button>
                        )}
                        {r.status !== "rejected" && (
                            <button
                                data-testid={`admin-review-reject-${r.id}`}
                                onClick={() => setStatus(r.id, "rejected")}
                                className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-ink/60 ring-1 ring-ink/10 transition-colors duration-300 hover:bg-red-50 hover:text-red-600"
                            >
                                <X className="h-3.5 w-3.5" /> Odrzuć
                            </button>
                        )}
                        {r.status !== "pending" && (
                            <button
                                data-testid={`admin-review-pending-${r.id}`}
                                onClick={() => setStatus(r.id, "pending")}
                                className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-ink/60 ring-1 ring-ink/10 transition-colors duration-300 hover:bg-mist"
                            >
                                Cofnij do oczekujących
                            </button>
                        )}
                    </div>
                </article>
            ))}
        </div>
    );
}

function Dashboard() {
    const { user, logout } = useAuth();
    const [leads, setLeads] = useState(null);
    const [tab, setTab] = useState("leads");

    const fetchLeads = useCallback(async () => {
        try {
            const { data } = await api.get("/leads");
            setLeads(data);
        } catch {
            toast.error("Nie udało się pobrać zgłoszeń");
        }
    }, []);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/leads/${id}`, { status });
            setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
            toast.success(`Status: ${STATUS_LABELS[status]}`);
        } catch {
            toast.error("Nie udało się zmienić statusu");
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/leads/${id}`);
            setLeads((prev) => prev.filter((l) => l.id !== id));
            toast.success("Zgłoszenie usunięte");
        } catch {
            toast.error("Nie udało się usunąć zgłoszenia");
        }
    };

    const counts = leads
        ? {
              new: leads.filter((l) => l.status === "new").length,
              in_progress: leads.filter((l) => l.status === "in_progress").length,
              done: leads.filter((l) => l.status === "done").length,
          }
        : { new: 0, in_progress: 0, done: 0 };

    return (
        <div className="min-h-screen bg-mist">
            <header className="sticky top-0 z-40 border-b border-ink/5 bg-white/85 backdrop-blur-xl">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
                    <div className="flex items-baseline gap-3">
                        <Link to="/" data-testid="admin-home-logo" className="font-display text-xl font-black tracking-tighter text-ink">
                            Handy<span className="text-gold">&rsquo;</span>s
                        </Link>
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">Panel zgłoszeń</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="mr-2 hidden text-xs text-ink/40 sm:block">{user.email}</span>
                        <button
                            data-testid="admin-refresh-btn"
                            onClick={fetchLeads}
                            className="rounded-full border border-ink/10 p-2.5 text-ink/60 transition-[background-color,color] duration-300 hover:bg-ink hover:text-white"
                            aria-label="Odśwież"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-xs font-semibold text-white transition-colors duration-300 hover:bg-gold hover:text-ink"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Wyloguj
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-6xl px-6 pt-6">
                <div className="inline-flex gap-1 rounded-full border border-ink/8 bg-white p-1">
                    {[["leads", "Zgłoszenia"], ["gallery", "Galeria"], ["reviews", "Opinie"]].map(([key, label]) => (
                        <button
                            key={key}
                            data-testid={`admin-tab-${key}`}
                            onClick={() => setTab(key)}
                            className={`rounded-full px-5 py-2 text-xs font-semibold transition-colors duration-300 ${
                                tab === key ? "bg-ink text-white" : "text-ink/55 hover:text-ink"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <main className="mx-auto max-w-6xl px-6 py-10" data-testid="admin-dashboard">
            {tab === "leads" && (
            <>
                <div className="mb-8 grid grid-cols-3 gap-4">
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                        <div key={key} data-testid={`admin-count-${key}`} className="rounded-2xl border border-ink/8 bg-white p-5 text-center">
                            <p className="font-display text-3xl font-black tracking-tight text-ink">{counts[key]}</p>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-ink/45">{label}</p>
                        </div>
                    ))}
                </div>

                {leads === null ? (
                    <div className="flex justify-center py-24">
                        <Loader2 className="h-8 w-8 animate-spin text-ink/30" />
                    </div>
                ) : leads.length === 0 ? (
                    <div className="flex flex-col items-center rounded-3xl border border-dashed border-ink/15 bg-white py-24 text-center">
                        <Inbox className="h-10 w-10 text-ink/20" />
                        <p className="mt-4 font-display text-xl font-bold text-ink">Brak zgłoszeń</p>
                        <p className="mt-1 text-sm text-ink/45">Nowe zgłoszenia z formularza pojawią się tutaj.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {leads.map((lead) => (
                            <article key={lead.id} data-testid={`admin-lead-${lead.id}`} className="rounded-3xl border border-ink/8 bg-white p-6 shadow-sm">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <h3 className="font-display text-lg font-bold text-ink">{lead.name}</h3>
                                            <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
                                                {STATUS_LABELS[lead.status] || lead.status}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-xs text-ink/40">
                                            {new Date(lead.created_at).toLocaleString("pl-PL", { dateStyle: "long", timeStyle: "short" })}
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-4 text-sm">
                                            <a href={`tel:${lead.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 font-medium text-ink transition-colors duration-200 hover:text-gold">
                                                <Phone className="h-3.5 w-3.5" /> {lead.phone}
                                            </a>
                                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 font-medium text-ink transition-colors duration-200 hover:text-gold">
                                                <Mail className="h-3.5 w-3.5" /> {lead.email}
                                            </a>
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`admin-lead-delete-${lead.id}`}
                                        onClick={() => remove(lead.id)}
                                        className="rounded-full border border-ink/10 p-2.5 text-ink/40 transition-[background-color,color,border-color] duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                        aria-label="Usuń zgłoszenie"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <p className="mt-4 rounded-2xl bg-mist p-4 text-sm leading-relaxed text-ink/70">{lead.message}</p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                        <button
                                            key={key}
                                            data-testid={`admin-lead-status-${key}-${lead.id}`}
                                            onClick={() => setStatus(lead.id, key)}
                                            disabled={lead.status === key}
                                            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                                                lead.status === key
                                                    ? "cursor-default bg-ink text-white"
                                                    : "bg-white text-ink/60 ring-1 ring-ink/10 hover:bg-gold hover:text-ink"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </>
            )}
            {tab === "gallery" && <GalleryPanel />}
            {tab === "reviews" && <ReviewsPanel />}
            </main>
        </div>
    );
}

export default function Admin() {
    const { user } = useAuth();

    if (user === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ink">
                <Loader2 className="h-8 w-8 animate-spin text-gold" />
            </div>
        );
    }
    if (user === false) return <LoginForm />;
    return <Dashboard />;
}
